import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from "react-router-dom";
import { Card, Button, Spin, Typography, Space, Alert, Result } from 'antd';
import {
    ShieldCheck,
    Loader2,
    CheckCircle2,
    RefreshCcw,
    Landmark,
    Lock,
    Plus,
    Zap,
    Clock1,
    ChevronUp,
    ChevronDown,
    Shield,
    ExternalLink,
    AlertCircle
} from 'lucide-react';
import { PluggyConnect } from 'react-pluggy-connect';
import { motion, AnimatePresence } from 'framer-motion';
import brasilOpen from '../../imagens/oepn-finance/brasil.png';
import "./style.css";
import { Method_POST_REST, Method_POST_CONTEXTO } from '../../service/api';

const { Title, Text } = Typography;

const PLUGGY_ERROR_MAPPING = {
    'PARAMETERS_NOT_PROVIDED': {
        title: 'Dados Incompletos',
        message: 'Por favor, reinicie o processo e preencha todos os campos solicitados pelo seu banco.',
        icon: <Shield size={48} color="#f59e0b" />
    },
    'ITEM_ALREADY_UPDATING': {
        title: 'Atualização em Curso',
        message: 'Já estamos buscando seus dados. Aguarde alguns instantes antes de tentar novamente.',
        icon: <Clock1 size={48} color="#3b82f6" />
    },
    'BEFORE_ALLOWED_FREQUENCY': {
        title: 'Limite de Atualizações',
        message: 'Para sua segurança, os bancos limitam a frequência de conexão. Tente novamente em algumas horas.',
        icon: <Clock1 size={48} color="#64748b" />
    },
    'TOO_MANY_CONSECUTIVE_ERRORS': {
        title: 'Muitas Tentativas',
        message: 'Tivemos várias falhas ao tentar conectar. Por favor, entre em contato com nosso suporte técnico.',
        icon: <AlertCircle size={48} color="#ef4444" />
    },
    'LOGIN_FAILURES': {
        title: 'Usuário ou Senha Incorretos',
        message: 'Não conseguimos acessar sua conta. Verifique seus dados bancários e tente novamente.',
        icon: <Lock size={48} color="#ef4444" />
    },
    'CONNECTOR_OFFLINE': {
        title: 'Banco Indisponível',
        message: 'O sistema do seu banco está fora do ar no momento. Tente novamente mais tarde.',
        icon: <Landmark size={48} color="#94a3b8" />
    },
    'ITEM_MFA_EXPIRED': {
        title: 'Código Expirado',
        message: 'O tempo para digitar o código de segurança acabou. Por favor, comece novamente.',
        icon: <Clock1 size={48} color="#f59e0b" />
    },
    'DEFAULT': {
        title: 'Algo deu errado',
        message: 'A conexão foi interrompida ou falhou. Por favor, tente novamente em instantes.',
        icon: <AlertCircle size={48} color="#ef4444" />
    }
};

const PaginaConexao = () => {
    const [accessToken, setAccessToken] = useState(null);
    const [status, setStatus] = useState('idle');
    const [errorData, setErrorData] = useState(PLUGGY_ERROR_MAPPING.DEFAULT);
    const { clienteId } = useParams();
    const navigate = useNavigate();
    const [showReasons, setShowReasons] = useState(false);

    const autenticarPluggy = useCallback(async () => {
        setStatus('loading');
        setErrorData(PLUGGY_ERROR_MAPPING.DEFAULT);

        try {
            const data = await Method_POST_REST(
                `/openfinance/conectar/${clienteId}`
            );

            if (!data?.connectToken) {
                throw new Error('TOKEN_ERROR');
            }

            setAccessToken(data.connectToken);
            setStatus('ready');
        } catch (err) {
            console.error('Erro na autenticação:', err);
            registrarErro('token', err);
            setErrorData({
                title: 'Erro de Conexão',
                message: 'Certifique-se que o backend local está ativo na porta 8080 ou tente novamente.',
                icon: <Shield size={48} color="#ef4444" />
            });
            setStatus('error');
        }
    }, [clienteId]);

    useEffect(() => {
        if (clienteId) {
            autenticarPluggy();
        }
    }, [autenticarPluggy, clienteId]);

    const handleOnSuccess = async (itemData) => {
        const connectionId = itemData?.item?.id || itemData?.id;
        console.log("Conexão realizada com sucesso na Pluggy. ID:", connectionId);

        if (connectionId) {
            // NÃO prendemos o cliente esperando o vínculo.
            //
            // Neste ponto o consentimento já foi dado e a conexão JÁ EXISTE na
            // Pluggy — o registro do nosso lado é consequência, e se falhar dá
            // pra refazer pelo painel admin (o item_id vai no log). Esperar só
            // criava o problema: o axios do projeto não define `timeout`
            // (default 0 = INFINITO), então quando a resposta não voltava a
            // promise nunca assentava e a tela ficava presa em "Quase lá".
            //
            // Dispara e segue. O erro, se houver, fica registrado no log.
            Method_POST_REST(
                `/openfinance/vincular-contas/${clienteId}`,
                { pluggyConnectionId: connectionId }
            )
                .then(() => console.log("Contas vinculadas no backend com sucesso."))
                .catch((e) => {
                    console.error("Erro ao vincular contas no backend:", e);
                    // O itemId é o dado mais valioso pra diagnosticar depois
                    // (dá pra usar no Diagnóstico do painel admin).
                    registrarErro('vinculo', e, connectionId);
                });

            setStatus('success');
        } else {
            setErrorData(PLUGGY_ERROR_MAPPING.DEFAULT);
            setStatus('error');
        }
    };

    /**
     * Manda a falha pro backend (que grava no log do Pantheon).
     * Best-effort: se o registro falhar, NÃO piora a tela do cliente — ele já
     * está num caminho de erro. Antes isso ficava só no console dele e a gente
     * nunca descobria o que tinha acontecido.
     */
    const registrarErro = (etapa, error, itemId) => {
        try {
            let bruto;
            try {
                bruto = JSON.stringify(error, Object.getOwnPropertyNames(error ?? {}));
            } catch {
                bruto = String(error);
            }
            Method_POST_REST(`/openfinance/erro/${clienteId}`, {
                etapa,
                codigo: error?.code ?? error?.error ?? error?.id ?? null,
                mensagem: error?.message ?? null,
                itemId: itemId ?? null,
                bruto,
            }).catch(() => { /* silencioso de propósito */ });
        } catch { /* nunca propaga */ }
    };

    const handleOnError = (error) => {
        console.error('Pluggy Widget Error:', error);
        const errorCode = error?.code || error?.error || error?.id;
        const mapped = PLUGGY_ERROR_MAPPING[errorCode] || PLUGGY_ERROR_MAPPING.DEFAULT;

        registrarErro('widget', error);
        setErrorData(mapped);
        setStatus('error');
    };

    const anim = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
        exit: { opacity: 0, scale: 0.96, transition: { duration: 0.2 } }
    };

    const isWidget = status === 'open_widget';

    return (
        <>
        <div
            className="of-page"
            style={{
                backgroundImage: `linear-gradient(rgba(151, 187, 223, 0.92), rgba(248, 250, 252, 0.92)), url(${brasilOpen})`,
            }}
        >
            <div className="of-page-inner">
                {/* Logo */}
                <motion.div
                    initial={{ opacity: 0, y: -15 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="of-logo-container"
                >
                    <img
                        src="https://www.meucontadoronline.com.br/assets/imagens/logo-branco.svg"
                        alt="Meu Contador Online"
                        className="of-logo-img"
                    />
                </motion.div>

                {/* Card */}
                <Card
                    className={`of-card ${isWidget ? 'of-card--widget' : ''}`}
                    bodyStyle={{ padding: 0 }}
                >
                    {/* Header */}
                    <div className="of-header">
                        <div className="of-header__icon">
                            {status === 'success'
                                ? <CheckCircle2 size={28} />
                                : <Landmark size={28} />}
                        </div>

                        <Space className="of-secure-badge">
                            <Lock size={13} color="#60a5fa" />
                            <Text className="of-secure-badge__text">
                                Conexão Segura e Criptografada
                            </Text>
                        </Space>

                        <Title level={3} className="of-header__title">
                            {status === 'success' ? 'Conexão Realizada!' : 'Open Finance'}
                        </Title>

                        <Text className="of-header__subtitle">
                            {status === 'success'
                                ? 'Seus dados foram integrados com sucesso.'
                                : 'Sincronize seus dados bancários automaticamente.'}
                        </Text>
                    </div>

                    {/* Body */}
                    <div className="of-body">
                        <AnimatePresence mode="wait">

                            {/* LOADING */}
                            {status === 'loading' && (
                                <motion.div key="loading" variants={anim} initial="hidden" animate="visible" exit="exit"
                                    className="of-state-container">
                                    <Spin indicator={<Loader2 className="animate-spin of-state-icon" style={{ color: '#005A9E' }} />} />
                                    <div style={{ marginTop: 20 }}>
                                        <Title level={5} style={{ margin: 0 }}>Preparando Ambiente</Title>
                                        <Text type="secondary" style={{ fontSize: 13 }}>Iniciando protocolo de segurança...</Text>
                                    </div>
                                </motion.div>
                            )}

                            {/* ERROR */}
                            {status === 'error' && (
                                <motion.div key="error" variants={anim} initial="hidden" animate="visible" exit="exit">
                                    <div className="of-state-container">
                                        <div style={{
                                            marginBottom: 20,
                                            display: 'flex',
                                            justifyContent: 'center'
                                        }}>
                                            {errorData.icon}
                                        </div>
                                        <Title level={4} style={{ color: '#1e293b', marginBottom: 8 }}>{errorData.title}</Title>
                                        <Text type="secondary" style={{ display: 'block', marginBottom: 32, fontSize: 14, padding: '0 20px' }}>
                                            {errorData.message}
                                        </Text>
                                        <Button
                                            type="primary"
                                            icon={<RefreshCcw size={16} />}
                                            onClick={autenticarPluggy}
                                            className="of-btn-primary"
                                            style={{ width: '80%' }}
                                        >
                                            Tentar Novamente
                                        </Button>
                                    </div>
                                </motion.div>
                            )}

                            {/* READY */}
                            {status === 'ready' && (
                                <motion.div key="ready" variants={anim} initial="hidden" animate="visible" exit="exit">
                                    <Space direction="vertical" size={20} style={{ width: '100%' }}>

                                        {/* Accordion */}
                                        <div>
                                            <Button
                                                type="text"
                                                onClick={() => setShowReasons(!showReasons)}
                                                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#f8fafc', borderRadius: 12 }}
                                            >
                                                <span style={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>Por que compartilhar seus dados?</span>
                                                {showReasons ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                            </Button>

                                            {showReasons && (
                                                <div style={{ marginTop: 10 }}>
                                                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                                        <div className="of-reason">
                                                            <div className="of-reason__icon" style={{ backgroundColor: '#ecfdf5', color: '#10b981' }}><Zap size={16} /></div>
                                                            <Text style={{ fontSize: 12 }}><b>Agilidade Extrema:</b> Importação automática sem erro humano.</Text>
                                                        </div>
                                                        <div className="of-reason">
                                                            <div className="of-reason__icon" style={{ backgroundColor: '#eff6ff', color: '#3b82f6' }}><ShieldCheck size={16} /></div>
                                                            <Text style={{ fontSize: 12 }}><b>Segurança Bancária:</b> Criptografia de ponta a ponta.</Text>
                                                        </div>
                                                        <div className="of-reason">
                                                            <div className="of-reason__icon" style={{ backgroundColor: '#fff7ed', color: '#f59e0b' }}><Clock1 size={16} /></div>
                                                            <Text style={{ fontSize: 12 }}><b>Tempo Real:</b> Movimentações sincronizadas diariamente.</Text>
                                                        </div>
                                                    </Space>
                                                </div>
                                            )}
                                        </div>

                                        <Alert
                                            message={<Text style={{ fontWeight: 700, color: '#854d0e' }}>Aviso de Privacidade</Text>}
                                            description="Você será redirecionado para o ambiente seguro para selecionar seu banco."
                                            type="warning"
                                            showIcon
                                            icon={<Shield size={18} />}
                                            style={{ borderRadius: 14, backgroundColor: '#fffbeb', border: '1px solid #fef3c7' }}
                                        />

                                        <Button
                                            type="primary"
                                            icon={<Plus size={20} />}
                                            onClick={() => setStatus('open_widget')}
                                            className="of-btn-primary"
                                            style={{ width: '100%' }}
                                        >
                                            CONECTAR BANCO AGORA
                                        </Button>


                                        <div className="of-trust-badge">
                                            <ShieldCheck size={13} color="#64748b" />
                                            <Text style={{ fontSize: 10, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                Certificado pelo Banco Central
                                            </Text>
                                        </div>
                                    </Space>
                                </motion.div>
                            )}

                            {/* WIDGET — indicador dentro do card */}
                            {status === 'open_widget' && (
                                <div key="widget" className="of-state-container">
                                    <Landmark size={36} color="#005A9E" style={{ marginBottom: 12 }} />
                                    <Title level={5} style={{ margin: 0 }}>Widget Aberto</Title>
                                    <Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 4 }}>Selecione seu banco na janela aberta</Text>
                                    <Button
                                        type="link"
                                        onClick={() => setStatus('ready')}
                                        style={{ color: '#005A9E', fontWeight: 600, fontSize: 13, marginTop: 12 }}
                                    >
                                        Cancelar
                                    </Button>
                                </div>
                            )}

                            {/* SUCCESS */}
                            {status === 'success' && (
                                <motion.div key="success" variants={anim} initial="hidden" animate="visible" exit="exit" className="of-state-container" style={{ padding: '0 0 16px' }}>
                                    <div className="of-success-badge" style={{ backgroundColor: '#f0fdf4', border: 'none' }}>
                                        <CheckCircle2 size={48} color="#10b981" />
                                    </div>

                                    <Title level={3} style={{ fontWeight: 800, marginBottom: 8, color: '#064e3b' }}>Tudo Pronto!</Title>
                                    <Text type="secondary" style={{ display: 'block', marginBottom: 28, padding: '0 16px', fontSize: 14 }}>
                                        Sua conta foi conectada com sucesso. Os dados já estão sendo processados pela nossa equipe.
                                    </Text>

                                    <div style={{ background: '#f0f9ff', padding: 'clamp(12px, 3vw, 20px)', borderRadius: 18, border: '1px solid #bae6fd', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                        <div style={{ textAlign: 'left', minWidth: 0 }}>
                                            <Text style={{ fontSize: 10, fontWeight: 800, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sincronização</Text>
                                            <div style={{ color: '#0c4a6e', fontWeight: 800, fontSize: 'clamp(14px, 2.5vw, 18px)' }}>Concluída</div>
                                        </div>
                                        <ShieldCheck size={28} color="#0369a1" style={{ flexShrink: 0 }} />
                                    </div>

                                    <Space direction="vertical" size={12} style={{ width: '100%', marginTop: 28 }}>
                                        <Button
                                            type="primary"
                                            icon={<Plus size={16} />}
                                            className="of-btn-primary"
                                            style={{ width: '100%' }}
                                            onClick={() => setStatus('ready')}
                                        >
                                            Conectar outro Banco
                                        </Button>

                                        <Button
                                            icon={<ExternalLink size={16} />}
                                            className="of-btn-secondary"
                                            onClick={() => window.location.href = 'https://portal.meucontadoronline.com.br/portal/#PortalLogin'}
                                        >
                                            Voltar ao Portal
                                        </Button>

                                        <Button
                                            type="link"
                                            onClick={() => window.close()}
                                            style={{ color: '#94a3b8', marginTop: 4, fontSize: 13 }}
                                        >
                                            Fechar esta página
                                        </Button>
                                    </Space>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Footer */}
                    <div className="of-footer">
                        <Space style={{ opacity: 0.6 }}>
                            <Lock size={12} color="#1e293b" />
                            <Text style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                LGPD Compliance • AES-256 Encryption
                            </Text>
                        </Space>
                    </div>
                </Card>

                {/* Disclaimer */}
                <Text className="of-disclaimer">
                    Ao conectar, você autoriza o acesso às suas informações financeiras conforme as normas do Banco Central do Brasil.
                </Text>
            </div>
        </div>
        {status === 'open_widget' && accessToken && createPortal(
            <PluggyConnect
                connectToken={accessToken}
                onSuccess={handleOnSuccess}
                onError={handleOnError}
                onClose={() => setStatus('ready')}
                allowFullscreen={true}
            />,
            document.body
        )}
        </>
    );
};

export default PaginaConexao;
